import { useEffect, useRef, useState } from "react";
import { Button, Typography } from "antd";
import { DeploymentUnitOutlined } from "@ant-design/icons";
import {
  ApartmentOutlined,
  ShopOutlined,
  AppstoreOutlined,
  BuildOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "./AddYours.css";

const { Title, Paragraph } = Typography;

/* ── scroll reveal ── */
function useInView(threshold = 0.06) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, vis];
}

/* ── hero entrance ── */
function useHeroAnim() {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setOn(true), 80);
    return () => clearTimeout(t);
  }, []);
  return (d = 0) => ({
    opacity: on ? 1 : 0,
    transform: on ? "translateY(0)" : "translateY(16px)",
    transition: `all .62s cubic-bezier(.16,1,.3,1) ${d}s`,
  });
}

const options = [
  {
    title: "Commercial Space",
    path: "/add-commercial-space",
    icon: <ApartmentOutlined />,
    desc: "List office spaces, retail shops, showrooms, and commercial properties for rent or sale.",
    thumb: "thumb-1",
    bar: "bar-blue",
  },
  {
    title: "Stall",
    path: "/add-stall",
    icon: <ShopOutlined />,
    desc: "Add stalls for food courts, exhibitions, kiosks, and local marketplace setups.",
    thumb: "thumb-2",
    bar: "bar-emerald",
  },
  {
    title: "Franchise",
    path: "/add-franchise",
    icon: <AppstoreOutlined />,
    desc: "Publish franchise opportunities and connect with investors and business partners.",
    thumb: "thumb-3",
    bar: "bar-violet",
  },
  {
    title: "Business",
    path: "/add-business",
    icon: <BuildOutlined />,
    desc: "Showcase your business for partnership, expansion, sale, or investment opportunities.",
    thumb: "thumb-4",
    bar: "bar-amber",
  },
  {
    title: "Ads Business",
    path: "/add-ads",   // 👈 route to your Ads form
    icon: <DeploymentUnitOutlined />,
    desc: "List billboard, vehicle ads, banners, and advertising spaces to reach more customers.",
    thumb: "thumb-5",
    bar: "bar-cyan",
  },
];

function AddYours() {
  const navigate = useNavigate();
  const anim = useHeroAnim();
  const [ref, vis] = useInView();

  return (
    <div className="ay">

      {/* ── Dark hero header ── */}
      <section className="ay__hero">
        <div className="ay__hero-inner">

          <div style={anim(0.05)}>
            <div className="ay__hero-pill">
              <span className="ay__hero-pill-dot" />
              Create a Listing
            </div>
          </div>

          <Title level={1} className="ay__hero-h1" style={anim(0.13)}>
            What would you like to list?
          </Title>

          <p className="ay__hero-sub" style={anim(0.2)}>
            Choose a category to create your listing. It's free, goes live
            instantly, and connects you directly with buyers and investors.
          </p>

        </div>
        <div className="ay__hero-fade" />
      </section>

      {/* ── Cards ── */}
      <div className="ay__body" ref={ref}>
        <div className="ay__inner">

          {/* Section divider */}
          <div className={`ay__label-row rv ${vis ? "in" : ""}`}>
            <div className="ay__label-line" />
            <span className="ay__label-text">Select a category</span>
            <div className="ay__label-line" />
          </div>

          <div className="ay__grid">
            {options.map((item, i) => (
              <div
                key={item.path}
                className={`ay-card rv d${i + 1} ${vis ? "in" : ""}`}
                onClick={() => navigate(item.path)}
              >
                {/* Top accent bar */}
                <div className={`ay-card__bar ${item.bar}`} />

                {/* Gradient thumb */}
                <div className={`ay-card__thumb ${item.thumb}`}>
                  <div className="ay-card__thumb-icon">{item.icon}</div>
                </div>

                {/* Body */}
                <div className="ay-card__body">
                  <Title level={4} className="ay-card__title">
                    {item.title}
                  </Title>
                  <Paragraph className="ay-card__desc">
                    {item.desc}
                  </Paragraph>

                  <div className="ay-card__foot">
                    <Button
                      type="primary"
                      className="ay-card__btn"
                      onClick={(e) => { e.stopPropagation(); navigate(item.path); }}
                    >
                      Continue <ArrowRightOutlined />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* ── Bottom trust strip ── */}
      <div className="ay__strip">
        <div className="ay__strip-inner">
          <div className="ay__strip-items">
            {[
              "Free to list",
              "Goes live instantly",
              "Direct buyer contact",
              "Zero commission",
            ].map((t) => (
              <div key={t} className="ay__strip-item">
                <span className="ay__strip-check">✓</span>
                {t}
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}

export default AddYours;